import { AlertMessage, ConfigurableText } from '@/app/components'
import { Button, Modal, Text } from '@/components'
import dayjs from 'dayjs'
import { ThermostatDemandResponseEvent } from '@/types'

type SaveMoneyInactiveModalProps = {
  isOpen: boolean
  onClose: () => void
  hasInactiveTimesIssue: boolean
  hasZeroCategoriesIssue: boolean
  hasDREventIssue: boolean
  hasProgramIssue: boolean
  inactiveTimesLines: string[]
  onEditInactiveTimes: () => void
  activeDrEvent?: ThermostatDemandResponseEvent
}

export default function SaveMoneyInactiveModal({
  isOpen,
  onClose,
  hasInactiveTimesIssue,
  hasZeroCategoriesIssue,
  hasDREventIssue,
  hasProgramIssue,
  inactiveTimesLines,
  onEditInactiveTimes,
  activeDrEvent,
}: SaveMoneyInactiveModalProps) {
  const costCategories = [
    { name: 'Negative Cost Hour', icon: '↓', color: 'text-green-500' },
    { name: 'Low Cost Hour', icon: '↓', color: 'text-green-500' },
    { name: 'Medium Cost Hour', icon: '→', color: 'text-orange-500' },
    { name: 'High Cost Hour', icon: '↑', color: 'text-red-500' },
  ]

  // Determine the title and subtitle based on which issues are present
  const getTitle = () => {
    if (hasProgramIssue) {
      return 'Save Money is currently paused by a program you are enrolled in.'
    }
    if (hasDREventIssue) {
      return 'Save Money is inactive due to DR event currently taking place.'
    }
    if (hasInactiveTimesIssue && hasZeroCategoriesIssue) {
      return 'Save Money is inactive because of your temperature shifts and inactive times.'
    }
    if (hasZeroCategoriesIssue) {
      return 'Save Money is inactive due to set temperature shifts'
    }
    if (hasInactiveTimesIssue) {
      return 'Save Money is off due to inactive times'
    }
    return 'Save Money is inactive'
  }

  const getSubtitle = () => {
    if (hasProgramIssue) {
      return 'This helps us get to know your current thermostat behaviors. Check back soon for updates!'
    }
    if (hasDREventIssue) {
      return 'Save Money will revert back to your set preferences when DR event is over.'
    }
    if (hasInactiveTimesIssue && hasZeroCategoriesIssue) {
      return 'To enable Save Money, edit your temperature shifts or inactive times.'
    }
    if (hasZeroCategoriesIssue) {
      return 'To enable Save Money, edit your temperature shifts'
    }
    if (hasInactiveTimesIssue) {
      return 'To enable Save Money, edit inactive times'
    }
    return 'To enable Save Money, make adjustments below'
  }

  const getButtonText = () => {
    if (hasProgramIssue) {
      return 'Close'
    }
    if (hasDREventIssue) {
      return 'Close'
    }
    if (hasInactiveTimesIssue && hasZeroCategoriesIssue) {
      return 'Edit Preferences'
    }
    if (hasZeroCategoriesIssue) {
      return 'Edit Temperature Shifts'
    }
    if (hasInactiveTimesIssue) {
      return 'Edit Inactive Times'
    }
    return 'Edit Preferences'
  }

  const handleButtonClick = () => {
    if (hasProgramIssue) {
      onClose()
      return
    }
    if (hasDREventIssue) {
      onClose()
      return
    }
    if (hasInactiveTimesIssue && !hasZeroCategoriesIssue) {
      onEditInactiveTimes()
    } else {
      onClose()
    }
  }

  return (
    <Modal
      id="save-money-inactive-modal"
      open={isOpen}
      onClose={onClose}
      title={getTitle()}
      maxWidth="28rem"
    >
      <div className="mb-6">
        <Text variant="body1" className="mb-6 text-gray-600 font-normal">
          {getSubtitle()}
        </Text>

        {/* Show DR event info section if applicable and not program override */}
        {hasDREventIssue && activeDrEvent && !hasProgramIssue && (
          <div className="mb-6">
            <AlertMessage variant="info">
              <div className="flex flex-col gap-1">
                <div className="flex gap-1 text-optiwatt-blue-500">
                  <ConfigurableText
                    variant="body2"
                    config={
                      "You're currently participating in an event between " +
                      dayjs(activeDrEvent.start_datetime).format('h:mm A') +
                      ' to ' +
                      dayjs(activeDrEvent.end_datetime).format('h:mm A') +
                      '.'
                    }
                  />
                </div>
              </div>
            </AlertMessage>
          </div>
        )}

        {/* Show inactive times section if applicable and not DR event or program override */}
        {hasInactiveTimesIssue && !hasDREventIssue && !hasProgramIssue && (
          <div className="mb-6">
            <AlertMessage variant="info">
              <div className="flex flex-col gap-1">
                <div className="flex gap-1 text-optiwatt-blue-500">
                  <ConfigurableText
                    variant="body2"
                    config={'Save Money will be inactive:'}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  {inactiveTimesLines.map((line, i) => (
                    <Text
                      key={i}
                      variant="body2"
                      className="text-optiwatt-blue-500"
                    >
                      {line}
                    </Text>
                  ))}
                </div>
              </div>
            </AlertMessage>
          </div>
        )}

        {/* Show temperature categories section if applicable and not DR event or program override */}
        {hasZeroCategoriesIssue && !hasDREventIssue && !hasProgramIssue && (
          <div className="p-4 mb-6 rounded-lg bg-gray-50">
            {costCategories.map((category, index) => (
              <div
                key={category.name}
                className={`flex items-center justify-between ${
                  index < costCategories.length - 1 ? 'mb-4' : ''
                }`}
              >
                <div className="flex items-center">
                  <span
                    className={`text-lg font-medium mr-3 ${category.color}`}
                  >
                    {category.icon}
                  </span>
                  <Text variant="body2" className="font-medium text-gray-800">
                    {category.name}
                  </Text>
                </div>
                <Text variant="body2" className="text-gray-600">
                  Shift to 0°
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal.Actions>
        <Button
          id="edit-preferences-button"
          onClick={handleButtonClick}
          variant="primary"
        >
          {getButtonText()}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
